import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
} from '@mui/material';
import { FolderZip, Close, Download, MailOutline } from '@mui/icons-material';

interface ZipExportDialogProps {
  open: boolean;
  onClose: () => void;
  year: number;
  month: number;
  onDownloadSuccess: (message: string) => void;
  onEmailSuccess: (message: string) => void;
}

/**
 * Dialog pour l'export complet au format ZIP
 * Combine téléchargement direct et envoi par email
 */
export const ZipExportDialog: React.FC<ZipExportDialogProps> = ({
  open,
  onClose,
  year,
  month,
  onDownloadSuccess,
  onEmailSuccess,
}) => {
  // États pour le téléchargement
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // États pour l'envoi par email
  const [email, setEmail] = useState('');
  const [sendCopyToSelf, setSendCopyToSelf] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleClose = () => {
    if (!downloadLoading && !emailLoading) {
      setEmail('');
      setSendCopyToSelf(true);
      setDownloadError(null);
      setEmailError(null);
      onClose();
    }
  };

  const getMonthName = (month: number): string => {
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return monthNames[month - 1];
  };

  /**
   * Télécharger le ZIP directement
   */
  const handleDownload = async () => {
    setDownloadLoading(true);
    setDownloadError(null);

    try {
      const expenseService = (await import('../../services/expenseService')).default;

      const blob = await expenseService.downloadZipExport(year, month);
      const filename = `Sersal_Export_${year}-${String(month).padStart(2, '0')}.zip`;
      expenseService.downloadBlobAsFile(blob, filename);

      onDownloadSuccess(`Export ZIP téléchargé avec succès : ${filename}`);
      handleClose();
    } catch (err: any) {
      console.error('Erreur lors du téléchargement:', err);
      setDownloadError(
        err.response?.data?.message ||
          'Erreur lors du téléchargement. Veuillez réessayer.'
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  /**
   * Valider l'email
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Envoyer le ZIP par email
   */
  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    // Validation de l'email
    if (!email.trim()) {
      setEmailError('Veuillez saisir une adresse email');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Veuillez saisir une adresse email valide');
      return;
    }

    setEmailLoading(true);

    try {
      const accountingService = (await import('../../services/accountingService')).default;

      await accountingService.sendReportByEmail(year, month, email, sendCopyToSelf);

      const successMessage = sendCopyToSelf
        ? 'Le rapport a été envoyé avec succès (une copie vous a été adressée).'
        : 'Le rapport a été envoyé avec succès.';

      onEmailSuccess(successMessage);
      handleClose();
    } catch (err: any) {
      console.error('Erreur lors de l\'envoi du rapport:', err);
      setEmailError(
        err.response?.data?.message ||
          'Erreur lors de l\'envoi du rapport. Veuillez réessayer.'
      );
    } finally {
      setEmailLoading(false);
    }
  };

  const isAnyLoading = downloadLoading || emailLoading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderZip />
          Export complet (ZIP)
        </Box>
        <IconButton onClick={handleClose} disabled={isAnyLoading} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Export pour <strong>{getMonthName(month)} {year}</strong> avec le CSV et tous les justificatifs.
        </Typography>

        {/* Section Téléchargement */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Télécharger sur cet appareil
          </Typography>

          {downloadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
              {downloadError}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleDownload}
            disabled={isAnyLoading}
            startIcon={downloadLoading ? <CircularProgress size={20} /> : <Download />}
            sx={{ py: 1.5 }}
          >
            {downloadLoading ? 'Téléchargement...' : 'Télécharger le ZIP'}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Section Envoi par Email */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Envoyer par email
          </Typography>

          {emailError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailError(null)}>
              {emailError}
            </Alert>
          )}

          <form onSubmit={handleSendEmail}>
            <TextField
              fullWidth
              label="Adresse email du destinataire"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="exemple@domaine.fr"
              disabled={isAnyLoading}
              helperText="Le fichier ZIP sera envoyé en pièce jointe"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendCopyToSelf}
                  onChange={(e) => setSendCopyToSelf(e.target.checked)}
                  disabled={isAnyLoading}
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: 28 },
                  }}
                />
              }
              label={
                <Typography variant="body1">
                  M'envoyer une copie par email
                </Typography>
              }
              sx={{
                alignItems: 'center',
                mb: 2,
                '& .MuiFormControlLabel-label': {
                  ml: 1,
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="outlined"
              size="large"
              disabled={isAnyLoading}
              startIcon={emailLoading ? <CircularProgress size={20} /> : <MailOutline />}
              sx={{ py: 1.5 }}
            >
              {emailLoading ? 'Envoi en cours...' : 'Envoyer par email'}
            </Button>
          </form>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isAnyLoading}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZipExportDialog;
